import React, { type ComponentProps } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';
import {
  CUSTOM_NAVIGATION_HEADER_HEIGHT,
  CustomNavigationHeader,
  theme,
  useResponsive,
} from '@sg/garnish';

// ─── Components ──────────────────────────────────────────────────────────────

const CustomizationNavigationHeaderContainer = (
  props: CustomizationSelectedIngredientsContainerProps,
) => {
  const { children, spacing = 'standard', ...restProps } = props;

  const { match } = useResponsive();
  const { top: safeAreaTopInset } = useSafeAreaInsets();

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(() => {
    const topOffset = Platform.select({
      android: safeAreaTopInset,
      default: 0,
    });

    return {
      minHeight: CUSTOM_NAVIGATION_HEADER_HEIGHT + topOffset,
    };
  }, [safeAreaTopInset]);

  const containerStyles = [
    containerDynamicStyles,
    match([
      undefined,
      spacing === 'narrow' ? styles.outerContainerNarrowSM : undefined,
    ]),
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <CustomNavigationHeader.Container
        palette="cream"
        safeAreaEdges={HEADER_SAFE_AREA_EDGES}
        gradientColors={HEADER_GRADIENT_COLORS}
        {...restProps}
      >
        {children}
      </CustomNavigationHeader.Container>
    </View>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const CustomizationNavigationHeaderContainerText = (
  props: CustomizationNavigationHeaderTextProps,
) => {
  const { children, ...restProps } = props;

  const { match } = useResponsive();

  return (
    <CustomNavigationHeader.Text
      textAlignment={match(['center', 'left'])}
      numberOfLines={1}
      style={styles.headerText}
      {...restProps}
    >
      {children}
    </CustomNavigationHeader.Text>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationNavigationHeader = {
  ...CustomNavigationHeader,
  Container: CustomizationNavigationHeaderContainer,
  Text: CustomizationNavigationHeaderContainerText,
};

// ─── Constants ───────────────────────────────────────────────────────────────

const HEADER_GRADIENT_COLORS = [theme.colors.CREAM, 'rgba(248, 247, 240, 0)'];
const HEADER_SAFE_AREA_EDGES: CustomizationSelectedIngredientsContainerProps['safeAreaEdges'] =
  Platform.select({ android: ['top'], default: [] });

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainerNarrowSM: {
    paddingHorizontal: theme.spacing['6'],
  },
  headerText: {
    flex: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationSelectedIngredientsContainerProps = {
  spacing?: 'standard' | 'narrow';
} & ComponentProps<typeof CustomNavigationHeader.Container>;

type CustomizationNavigationHeaderTextProps = ComponentProps<
  typeof CustomNavigationHeader.Text
>;
